import { useQuery } from '@tanstack/react-query';
import { Link2, ArrowDownToLine, ArrowUpFromLine } from 'lucide-react';
import { Card, CardTitle, CardDescription } from './Card';
import { Skeleton } from './EmptyState';
import { StatusPill } from './StatusPill';
import { api } from '@/lib/api';

interface RelatedDoc {
  doctype: string;
  document_id: string;
  direction: 'upstream' | 'downstream';
  qty_linked?: string;
  amount_linked?: string;
}

interface Props {
  doctype: string;
  documentID: string;
}

const DOCTYPE_PATH: Record<string, string> = {
  sales_invoice:    '/accounting/sales-invoices',
  purchase_invoice: '/accounting/purchase-invoices',
  purchase_order:   '/accounting/purchase-orders',
  sales_order:      '/accounting/sales-orders',
  purchase_receipt: '/stock/purchase-receipts',
  material_request: '/accounting/material-requests',
  payment_entry:    '/accounting/payment-entries',
  journal_entry:    '/accounting/journal-entries',
  asset:            '/assets/assets',
  asset_movement:   '/assets/asset-movements',
  work_order:       '/manufacturing/work-orders',
  bom:              '/manufacturing/boms',
  stock_entry:      '/stock/stock-entries',
};

function prettyDoctype(d: string): string {
  return d.replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
}

export function RelatedDocsPanel({ doctype, documentID }: Props) {
  const { data, isLoading } = useQuery({
    queryKey: ['related-docs', doctype, documentID],
    queryFn:  () => api<{ items: RelatedDoc[] }>(`/related-docs?doctype=${doctype}&document_id=${documentID}`),
    enabled:  !!documentID,
  });
  const items = data?.items ?? [];

  if (!isLoading && items.length === 0) return null;

  return (
    <Card padded={false}>
      <div className="px-5 py-3 border-b border-hairline">
        <CardTitle>
          <Link2 className="size-4 inline mr-1.5 text-accent" /> Related transactions
        </CardTitle>
        <CardDescription>Documents linked to this record via the fulfilment chain.</CardDescription>
      </div>

      {isLoading ? (
        <div className="p-5"><Skeleton className="h-16 w-full" /></div>
      ) : (
        <ul className="divide-y divide-hairline">
          {items.map((r) => {
            const path = DOCTYPE_PATH[r.doctype];
            const href = path ? `${path}/${r.document_id}` : '#';
            const Icon = r.direction === 'upstream' ? ArrowUpFromLine : ArrowDownToLine;
            return (
              <li key={`${r.doctype}:${r.document_id}`} className="px-5 py-3 flex items-center justify-between gap-3">
                <a href={href} className="flex items-center gap-2 min-w-0 hover:underline">
                  <Icon className="size-3.5 text-text-tertiary shrink-0" />
                  <span className="text-caption text-text-secondary">{prettyDoctype(r.doctype)}</span>
                  <span className="font-mono text-caption text-text-primary truncate">{r.document_id.slice(0, 16)}…</span>
                </a>
                <StatusPill tone="neutral" withDot={false}>
                  {r.direction === 'upstream' ? 'fulfils' : 'fulfilled by'}
                </StatusPill>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}
